import { ChangeEventHandler, FC } from 'react';

import { Box, BoxProps, FormLabel, InputAdornment, TextField } from '@mui/material';
import { useField } from 'formik';

import { getDeviceType } from 'src/utils/getDeviceType';
export type OptionType = {
    value: string;
    label: string;
};

type CustomInputProps = {
    name: string;
    value: string | number;
    onChange: ChangeEventHandler<HTMLInputElement>;
    label?: string;
    subLabel?: string;
    placeholder?: string;
    boxStyling?: BoxProps;
    error?: string;
    type?: string;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    alignInput?: 'center' | 'left' | 'right' | 'top';
    currency?: string;
    endAdornment?: string;
    dataTestId?: string;
    maxLength?: number;
    min?: number;
    isMultiline?: boolean;
    textFieldBorderRadius?: string;
    textFieldPadding?: string;
    defaultValue?: string;
    isReadOnly?: boolean;
};

export const CustomInput: FC<CustomInputProps> = ({
    name,
    value,
    onChange,
    label = '',
    subLabel,
    boxStyling,
    error,
    placeholder = '',
    type = 'text',
    disabled = false,
    size = 'medium',
    alignInput = 'left',
    currency,
    endAdornment,
    dataTestId = `${name}-input`,
    maxLength,
    min,
    isMultiline = false,
    textFieldBorderRadius = '8px',
    textFieldPadding = '15.5px 14px',
    defaultValue = '',
    isReadOnly,
}) => {
    const [{ onBlur }, { touched }] = useField(name);

    const borderColor = touched && error ? 'red' : '#F39E4E';
    const device = getDeviceType();
    return (
        <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
            {label && (
                <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                    {label}
                    {subLabel && (
                        <span style={{ fontWeight: 400, fontSize: '12px', marginLeft: '4px' }}>
                            {subLabel}
                        </span>
                    )}
                </FormLabel>
            )}

            <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                defaultValue={defaultValue}
                placeholder={placeholder ? placeholder : label ? `Enter ${label}` : ''}
                size={size !== 'large' ? size : null}
                multiline={isMultiline}
                maxRows={device === 'mobile' ? 20 : device === 'tablet' ? 24 : 27}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor,
                        },
                        '&:hover fieldset': {
                            borderColor,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor,
                        },
                    },
                    '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                    },
                    '& .MuiOutlinedInput-input': {
                        textAlign: alignInput,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: textFieldBorderRadius,
                        padding: textFieldPadding,
                    },
                }}
                type={type}
                autoComplete="off"
                disabled={disabled}
                inputProps={{
                    'data-testid': dataTestId,
                    startAdornment: currency ? (
                        <InputAdornment position="start">{currency}</InputAdornment>
                    ) : undefined,
                    maxLength: maxLength,
                    min: min,
                }}
                InputProps={{
                    endAdornment: <InputAdornment position="start">{endAdornment}</InputAdornment>,
                }}
                slotProps={{
                    input: {
                        readOnly: isReadOnly,
                    },
                }}
            />
            {error && touched && <label className="error">{error}</label>}
        </Box>
    );
};
